import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import useAPI from '../../../../../hooks/useAPI';

import SUTextField from '../../Components/SUTextField';
import NodeModal, { NodeModalButton } from '../../Components/NodeModal';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import ace from 'ace-builds/src-noconflict/ace';

// Disable workers to avoid loading issues
ace.config.set('useWorker', false);

import styledComponent from 'styled-components';
import Select from 'react-select';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import TriggerTypeField from '../../Components/TriggerTypeField';
import useApprovedTemplates from '../../Components/useApprovedTemplates';
import useNodeEditor from '../../Components/useNodeEditor';
import ValueFiltersEditor from '../../Components/ValueFiltersEditor';
import { getFlowsWildcardError } from '../../Components/flowTriggerRules';
import { renderVariableChips } from '../../Components/VariableChips';
import { sendToast } from '../../../../plugins/toastAll';
import {
  parseParamLines,
  formatParamLines,
  moveItem,
  copyToClipboard,
} from '../../Components/paramsBulk';
import { NODE_THEMES } from '../../Components/NodeTheme';
import {
  FieldLabel,
  FormSection,
  ChipButton,
  SectionToolbar,
  StepSection,
  InfoCallout,
  ListRow,
  EmptyHint,
  CollapsibleSection,
  focusFirstInvalidField, KitSelect } from '../../Components/NodeFormKit';

import { BsTrash3, BsGearFill } from 'react-icons/bs';
import { LuClipboardPaste, LuCopy, LuEraser } from 'react-icons/lu';
import { IoIosArrowDropdown, IoIosArrowDropup, IoMdAdd } from 'react-icons/io';
import { FaCheckCircle } from 'react-icons/fa';
import { BsDatabaseFillAdd } from 'react-icons/bs';
import { BiSolidError, BiTransfer } from 'react-icons/bi';

const QUERY_API_NODE_COLOR = '#7ECCD8';

const describeResponseTransform = (param) => {
  if (param.source === 'manual') return `Valor fijo: ${param.manualValue || '(vacío)'}`;
  if (param.source === 'clear') return 'Limpia el campo';
  const count = (param.filters || []).length;
  return count ? `${count} filtro${count > 1 ? 's' : ''} de limpieza` : null;
};

const QueryApi = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  handleConnectionTestData,
  handleModalConnectionTest,
  handleConnectionTestDataParams,
  watchSelectedNode,
  valueSelectedVariable,
  typeInputValue,
  typeInput,
  resetVariable,
  contextVariables,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const { urlAPI_2, fetchWithAuth } = useAPI();
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    resetField,
    setError,
  } = useForm({
    user_response: null,
    shouldUnregister: false,
  });

  const { templates, loading: loadingTemplates } = useApprovedTemplates();

  useEffect(() => {
    register('variables');
  }, [register]);

  const { handleInputChange, loadData, saveNode, getIsDirty } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });

  const [bulkTarget, setBulkTarget] = useState(null);
  const [bulkText, setBulkText] = useState('');
  const [bulkReplace, setBulkReplace] = useState(false);

  const [errorProtocol, setErrorProtocol] = useState(false);
  const [errorAuth, setErrorAuth] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [modalParams, setModalParams] = useState(false);
  const [parameterType, setParameterType] = useState(null);
  
  // Estado para almacenar temporalmente los datos a editar
  const [tempEditData, setTempEditData] = useState(null);
  const [responseFilters, setResponseFilters] = useState([]);

  const [dropdownRegularExpression, setDropdownRegularExpression] = useState(false);
  const [dropdownHeaders, setDropdownHeaders] = useState(false);
  const [dropdownRequest, setDropdownRequest] = useState(false);
  const [dropdownRequestBody, setDropdownRequestBody] = useState(false);
  const [dropdownResponse, setDropdownResponse] = useState(false);


  // FUNCTIONS
  const handleDropdown = (property) => {
    switch (property) {
      case 'dropdownHeaders': setDropdownHeaders(!dropdownHeaders); break;
      case 'dropdownRequest': setDropdownRequest(!dropdownRequest); break;
      case 'dropdownRequestBody': setDropdownRequestBody(!dropdownRequestBody); break;
      case 'dropdownResponse': setDropdownResponse(!dropdownResponse); break;
      case 'dropdownRegularExpression': setDropdownRegularExpression(!dropdownRegularExpression); break;
      default: console.error(`Property ${property} not recognized`);
    }
  };


  const saveData = (values) => {
    if (!selectedNode.data.protocol) {
      setErrorProtocol(true);
      return;
    }
    if (!selectedNode.data.auth) {
      setErrorAuth(true);
      return;
    }
    const flowsWildcardError = getFlowsWildcardError(
      selectedNode.data?.trigger_type,
      values.user_response,
      selectedNode.data?.flow_template_id
    );
    if (flowsWildcardError) {
      setError('user_response', { type: 'manual', message: flowsWildcardError });
      return;
    }

    setLoadingUpdate(true);

    let variablesValue = values.variables;
    if (typeof variablesValue === 'object') {
      try {
        variablesValue = JSON.stringify(variablesValue, null, 2);
      } catch (e) {}
    }

    const data = {
      title: values.title,
      user_response: values.user_response,
      bot_response: values.bot_response,
      trigger_type: selectedNode.data?.trigger_type || 'message',
      url_api: values.url_api,
      user: values?.user ?? null,
      password: values?.password ?? null,
      variables: variablesValue,
      advanced: {
        ...selectedNode.data?.advanced,
        regularExpressions: {
          ...selectedNode.data?.advanced?.regularExpressions,
          errorMessage: values.regular_expressions_error_message,
        },
      },
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
    });
  };

  const getData = () => {
    let variablesValue = selectedNode.data?.variables ?? null;
    if (typeof variablesValue === 'string') {
      try {
        const parsed = JSON.parse(variablesValue);
        variablesValue = JSON.stringify(parsed, null, 2);
      } catch (e) { }
    }
    
    let objectData = {
      title: selectedNode.data?.title ?? null,
      user_response: selectedNode.data?.user_response ?? null,
      bot_response: selectedNode.data?.bot_response ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
      url_api: selectedNode.data?.url_api ?? null,
      user: selectedNode.data?.user ?? null,
      password: selectedNode.data?.password ?? null,
      variables: variablesValue,
      regular_expressions_error_message: selectedNode.data?.advanced?.regularExpressions?.errorMessage ?? null,
    };
    loadData(objectData, {});
  };

  // Prepara los datos pero NO los setea todavía
  const handleModalParams = (type, data) => {
    setParameterType(type);
    setTempEditData(data); // Guardamos datos en estado temporal
    setModalParams(true);  // Abrimos modal
  };

  // EFECTO PARA LLENAR EL FORMULARIO DEL MODAL AL ABRIRSE
  useEffect(() => {
    if (modalParams && parameterType) {
      if (tempEditData) {
        // MODO EDICIÓN: Llenamos los campos
        setValue(`${parameterType}_key`, tempEditData.key ?? '');
        setValue(`${parameterType}_value`, tempEditData.value ?? '');
        setValue(`${parameterType}_index`, tempEditData.index ?? '');
        
        if(parameterType === 'response') {
           setValue(`${parameterType}_source`, tempEditData.source ?? 'user_message');
           setValue(`${parameterType}_manualValue`, tempEditData.manualValue ?? '');
           setResponseFilters(Array.isArray(tempEditData.filters) ? tempEditData.filters : []);
        }
      } else {
        // MODO CREAR: Limpiamos los campos explícitamente
        setValue(`${parameterType}_key`, '');
        setValue(`${parameterType}_value`, '');
        setValue(`${parameterType}_index`, '');
        if(parameterType === 'response') {
          setValue(`${parameterType}_source`, 'user_message');
          setValue(`${parameterType}_manualValue`, '');
          setResponseFilters([]);
        }
      }
    }
  }, [modalParams, parameterType, tempEditData, setValue]);

  const handleDataParams = (values) => {
    const dataKeyMap = {
      header: 'headers',
      request: 'requestParams',
      body: 'requestParamsBody',
      response: 'responseParams',
    };

    const dataKey = dataKeyMap[parameterType];
    if (!dataKey) return;

    const key = values[`${parameterType}_key`];
    const value = values[`${parameterType}_value`];
    const source = values[`${parameterType}_source`] || 'user_message';
    const manualValue = source === 'manual' ? values[`${parameterType}_manualValue`] || '' : '';
    const filters = parameterType === 'response' && source === 'user_message' ? responseFilters : [];

    const updateNodeData = (updatedData) => {
      handleSelectedNode((prevInputValue) => ({
        ...prevInputValue,
        data: { ...prevInputValue.data, [dataKey]: updatedData },
      }));
    };

    const currentData = [...selectedNode.data[dataKey]];
    const payload = { key, value, source, manualValue, filters };
    
    // Obtenemos el índice del input oculto
    const indexStr = values[`${parameterType}_index`];
    // Convertimos a número si no está vacío, sino undefined
    const index = indexStr !== '' && indexStr !== undefined ? parseInt(indexStr, 10) : undefined;

    if (!editModal || index === undefined || isNaN(index)) {
      updateNodeData([...currentData, payload]);
    } else {
      if (currentData[index]) {
        currentData[index] = payload;
        updateNodeData(currentData);
      }
    }

    setModalParams(false);
    setEditModal(false);
    setTempEditData(null);
    setParameterType(null);
  };

  useEffect(() => {
    if (typeInput == 'node') {
      setValue(typeInputValue, getValues(typeInputValue) + valueSelectedVariable);
      resetVariable();
    }
  }, [valueSelectedVariable]);

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  const PARAM_SECTIONS = {
    header: { dataKey: 'headers', noun: 'cabecera', nounPlural: 'cabeceras' },
    request: { dataKey: 'requestParams', noun: 'parámetro', nounPlural: 'parámetros' },
    body: { dataKey: 'requestParamsBody', noun: 'parámetro', nounPlural: 'parámetros' },
    response: { dataKey: 'responseParams', noun: 'mapeo', nounPlural: 'mapeos' },
  };

  const setParams = (dataKey, list) => {
    handleSelectedNode((prev) => ({
      ...prev,
      data: { ...prev.data, [dataKey]: list },
    }));
  };

  const moveParam = (dataKey, from, to) => {
    setParams(dataKey, moveItem(selectedNode.data?.[dataKey], from, to));
  };

  const copyParams = async (type) => {
    const { dataKey, nounPlural } = PARAM_SECTIONS[type];
    const rows = selectedNode.data?.[dataKey] || [];
    if (!rows.length) return;
    const ok = await copyToClipboard(formatParamLines(rows));
    sendToast({
      type: ok ? 'success' : 'error',
      message: ok
        ? `${rows.length} ${nounPlural} copiados al portapapeles.`
        : 'No se pudo copiar al portapapeles.',
    });
  };

  const openBulk = (type) => {
    setBulkTarget(type);
    setBulkText('');
    setBulkReplace(false);
  };

  const closeBulk = () => {
    setBulkTarget(null);
    setBulkText('');
    setBulkReplace(false);
  };

  const bulkRows = bulkTarget ? parseParamLines(bulkText) : [];

  const applyBulk = () => {
    if (!bulkTarget || !bulkRows.length) return;
    const { dataKey, nounPlural } = PARAM_SECTIONS[bulkTarget];
    const current = selectedNode.data?.[dataKey] || [];
    setParams(dataKey, bulkReplace ? bulkRows : [...current, ...bulkRows]);
    sendToast({
      type: 'success',
      message: `${bulkRows.length} ${nounPlural} ${
        bulkReplace ? 'reemplazaron los anteriores' : 'agregados'
      }.`,
    });
    closeBulk();
  };

  const countOf = (dataKey) => selectedNode.data?.[dataKey]?.length || 0;

  const triggerLabel =
    { message: 'Mensaje', event: 'Evento', flows: 'Flujos' }[
      selectedNode.data?.trigger_type || 'message'
    ] || 'Mensaje';

  const stepState = {
    trigger:
      selectedNode.data?.first || selectedNode.data?.user_response
        ? 'done'
        : 'error',
    request:
      selectedNode.data?.url_api && selectedNode.data?.protocol?.id
        ? 'done'
        : 'error',
    response:
      countOf('responseParams') || selectedNode.data?.bot_response
        ? 'done'
        : 'pending',
  };

  const stepSummary = {
    trigger: selectedNode.data?.first
      ? 'Nodo inicial'
      : selectedNode.data?.user_response
      ? `${triggerLabel} · ${selectedNode.data.user_response}`
      : 'Falta la respuesta del cliente',
    request: selectedNode.data?.url_api
      ? [
          selectedNode.data?.protocol?.label || 'Sin protocolo',
          countOf('headers') ? `${countOf('headers')} cabeceras` : null,
          countOf('requestParams')
            ? `${countOf('requestParams')} de petición`
            : null,
          countOf('requestParamsBody')
            ? `${countOf('requestParamsBody')} de cuerpo`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : 'Falta la URL de la API',
    response: countOf('responseParams')
      ? `${countOf('responseParams')} ${
          countOf('responseParams') === 1 ? 'mapeo' : 'mapeos'
        } de respuesta`
      : 'Sin mapeos de respuesta',
  };

  const clearParams = (type) => {
    const { dataKey, nounPlural } = PARAM_SECTIONS[type];
    const total = selectedNode.data?.[dataKey]?.length || 0;
    if (!total) return;
    setParams(dataKey, []);
    sendToast({
      type: 'success',
      message: `Se quitaron ${total} ${nounPlural}.`,
    });
  };

  const sectionActions = (type) => {
    const { dataKey, noun, nounPlural } = PARAM_SECTIONS[type];
    const total = selectedNode.data?.[dataKey]?.length || 0;
    return (
      <SectionToolbar
        addLabel={`Agregar ${noun}`}
        onAdd={() => handleModalParams(type)}
        actions={[
          {
            label: 'Pegar lista',
            description: `Varios ${nounPlural} de una vez`,
            icon: LuClipboardPaste,
            onClick: () => openBulk(type),
          },
          {
            label: 'Copiar todo',
            description: total
              ? `${total} ${nounPlural} al portapapeles`
              : 'No hay nada para copiar',
            icon: LuCopy,
            disabled: !total,
            onClick: () => copyParams(type),
          },
          {
            label: 'Vaciar sección',
            description: total ? `Quita los ${total}` : 'Ya está vacía',
            icon: LuEraser,
            tone: 'danger',
            disabled: !total,
            onClick: () => clearParams(type),
          },
        ]}
      />
    );
  };

  const onError = (formErrors) => {
    if (formErrors.regular_expressions_error_message) {
      setDropdownRegularExpression(true);
    }
    focusFirstInvalidField(formErrors);
  };

  return (
    <>
      <NodeEditorChrome
        icon={BsDatabaseFillAdd}
        color={QUERY_API_NODE_COLOR}
        typeName="Consulta API"
        title={truncateText(selectedNode.data.title)}
        onRemove={() => removeNode(selectedNode.id)}
        onMouseDown={onMouseDown}
        isDragging={isDragging}
        onClose={() => {
          handleSelectedNode(null);
          setDropdownRegularExpression(false);
        }}
        formId="queryApiForm"
        saving={loadingUpdate}
        isDark={isDark}
        getIsDirty={getIsDirty}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      >
        <form id="queryApiForm" onSubmit={handleSubmit(saveData, onError)} style={{ width: '100%' }}>
          <div>
            <StepSection
              index={1}
              title="Disparador"
              summary={stepSummary.trigger}
              state={stepState.trigger}
            >
            <div className="col p-0">
              <SUTextField id="title" name="title" label="Título:" type="text" selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                {...register('title', { required: { value: !modalParams, message: 'Campo requerido.' }, pattern: modalParams ? undefined : { value: /\S+/, message: 'No puede ser solo espacios en blanco' } })}
              />
            </div>

            {!selectedNode.data?.first && (<>
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>

            {selectedNode.data?.trigger_type === 'flows' && (
              <div className="col p-0 mt-4">
                <label htmlFor="flow_template_id" style={{ color: theme.textSecondary, fontWeight: 600 }}>
                  Plantilla esperada (opcional):
                </label>
                <div className="su-control-group d-flex">
                  <Select
                    styles={isDark == 'true' ? customStylesDark : customStyles}
                    className="form-control basic-multi-select"
                    placeholder={loadingTemplates ? 'Cargando plantillas...' : 'Seleccione una plantilla'}
                    name="flow_template_id"
                    value={
                      templates.find(
                        (templateOption) =>
                          String(templateOption.value) === String(selectedNode.data?.flow_template_id || '')
                      ) || null
                    }
                    onChange={(option) => {
                      handleInputChange('flow_template_id', option ? String(option.value) : null);
                      handleInputChange('flow_template_name', option ? option.label : null);
                    }}
                    options={templates}
                    isClearable
                    isLoading={loadingTemplates}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    getOptionLabel={(option) => (
                      <div className="option d-flex" style={{ fontSize: '15px' }}>{option.label}</div>
                    )}
                    getOptionValue={(option) => option.value}
                  />
                </div>
                <InfoCallout>
                  Si seleccionas una plantilla, este nodo tipo flujo solo se
                  activará cuando el usuario responda a esa plantilla. Con * (cualquier respuesta) la plantilla es obligatoria y el nodo solo se activa dentro de las 24 h siguientes a su envío.
                </InfoCallout>
              </div>
            )}

            <div className="col p-0 mt-4">
              <SUTextField id="user_response" name="user_response" label="Respuesta del cliente:" hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ." type="textArea" selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                {...register('user_response', { required: { value: !modalParams, message: 'Campo requerido.' }, pattern: modalParams ? undefined : { value: /\S+/, message: 'No puede ser solo espacios en blanco' } })}
              />
              <InfoCallout title="Importante:">
                Con el signo * funcionará para cualquier respuesta.
              </InfoCallout>
            </div>
            </>)}
            </StepSection>

            <StepSection
              index={2}
              title="Petición"
              summary={stepSummary.request}
              state={stepState.request}
            >
            <div className="col p-0">
              <SUTextField id="url_api" name="url_api" label="URL API externa:" type="text" selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors} showVariables contextVariables={contextVariables}
                {...register('url_api', { required: { value: !modalParams, message: 'Campo requerido.' }, pattern: modalParams ? undefined : { value: /\S+/, message: 'No puede ser solo espacios en blanco' } })}
              />
            </div>

            <div className="col p-0 mt-4">
              <label htmlFor="protocol" style={{ color: theme.textSecondary, fontWeight: 500 }}>Protocolo HTTP: </label>
              <div>
                <Select
                  styles={isDark == 'true' ? customStylesDark : customStyles}
                  className="form-control basic-multi-select"
                  placeholder="Seleccione..."
                  name="protocol"
                  value={selectedNode.data.protocol}
                  onChange={(protocol) => { handleInputChange('protocol', protocol); setErrorProtocol(false); }}
                  options={[{ label: 'GET', id: 'GET' }, { label: 'POST', id: 'POST' }, { label: 'PUT', id: 'PUT' }, { label: 'DELETE', id: 'DELETE' }]}
                  classNamePrefix="select"
                  noOptionsMessage={() => 'Sin opciones'}
                  getOptionLabel={(option) => (<div className="option d-flex" style={{ fontSize: '15px' }}>{option.label}</div>)}
                  getOptionValue={(option) => option.label}
                />
                {errorProtocol && <Alert>* Campo requerido</Alert>}
              </div>
            </div>

            <div className="col p-0 mt-4">
              <label htmlFor="auth" style={{ color: theme.textSecondary, fontWeight: 500 }}>Tipo de autenticación: </label>
              <div>
                <Select
                  styles={isDark == 'true' ? customStylesDark : customStyles}
                  className="form-control basic-multi-select"
                  placeholder="Seleccione..."
                  name="auth"
                  value={selectedNode.data.auth}
                  onChange={(item) => { handleInputChange('auth', item); setErrorAuth(false); }}
                  options={[{ label: 'Ninguna', id: 'none' }, { label: 'Usuario/Contraseña', id: 'user_password' }]}
                  classNamePrefix="select"
                  noOptionsMessage={() => 'Sin opciones'}
                  getOptionLabel={(option) => (<div className="option d-flex" style={{ fontSize: '15px' }}>{option.label}</div>)}
                  getOptionValue={(option) => option.label}
                />
                {errorAuth && <Alert>* Campo requerido</Alert>}
              </div>
            </div>

            {selectedNode.data.auth && selectedNode.data.auth == 'user_password' && (
              <>
                <div className="col p-0">
                  <SUTextField id="user" name="user" label="Usuario:" type="text" selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                    {...register('user', { required: { value: !modalParams, message: 'Campo requerido.' } })}
                  />
                </div>
                <div className="col p-0">
                  <SUTextField id="password" name="password" label="Contraseña:" type="text" selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                    {...register('password', { required: { value: !modalParams, message: 'Campo requerido.' } })}
                  />
                </div>
              </>
            )}

            {/* --- SECCIÓN HEADERS (CORREGIDO type="button") --- */}
            <div className="col p-0 mt-4">
              <CollapsibleSection
                title="Cabeceras (Opcional)"
                count={selectedNode.data.headers?.length || 0}
                open={dropdownHeaders}
                onToggle={() => handleDropdown('dropdownHeaders')}
              >
                        {sectionActions('header')}
                        <div className="mt-2">
                          {selectedNode.data.headers.length ? (
                            selectedNode.data.headers.map((head, index) => (
                              <ListRow
                                key={index}
                                dragIndex={index}
                                onReorder={(from, to) => moveParam('headers', from, to)}
                                revealActions
                                onRemove={() => {
                                  const filter = selectedNode.data.headers.filter((v, i) => i !== index);
                                  handleSelectedNode((prev) => ({ ...prev, data: { ...prev.data, headers: filter } }));
                                }}
                                removeTitle="Eliminar cabecera"
                              >
                                <div
                                  className="pointer"
                                  title="Editar"
                                  style={{ overflowWrap: 'anywhere' }}
                                  onClick={() => { setEditModal(true); handleModalParams('header', { ...head, index }); }}
                                >
                                  <div style={{ fontWeight: 600 }}>{head.key}</div>
                                  <div style={{ color: theme.textSecondary }}>{head.value}</div>
                                </div>
                              </ListRow>
                            ))
                          ) : (
                            <EmptyHint>Sin cabeceras.</EmptyHint>
                          )}
                        </div>
              </CollapsibleSection>
            </div>

            {/* --- SECCIÓN REQUEST PARAMS (CORREGIDO type="button") --- */}
            <div className="col p-0 mt-4">
              <CollapsibleSection
                title="Parámetros de petición (Opcional)"
                count={selectedNode.data.requestParams?.length || 0}
                open={dropdownRequest}
                onToggle={() => handleDropdown('dropdownRequest')}
              >
                        {sectionActions('request')}
                        <div className="mt-2">
                          {selectedNode.data.requestParams.length ? (
                            selectedNode.data.requestParams.map((req, index) => (
                              <ListRow
                                key={index}
                                dragIndex={index}
                                onReorder={(from, to) => moveParam('requestParams', from, to)}
                                revealActions
                                onRemove={() => {
                                  const filter = selectedNode.data.requestParams.filter((v, i) => i !== index);
                                  handleSelectedNode((prev) => ({ ...prev, data: { ...prev.data, requestParams: filter } }));
                                }}
                                removeTitle="Eliminar parámetro"
                              >
                                <div
                                  className="pointer"
                                  title="Editar"
                                  style={{ overflowWrap: 'anywhere' }}
                                  onClick={() => { setEditModal(true); handleModalParams('request', { ...req, index }); }}
                                >
                                  <div style={{ fontWeight: 600 }}>{req.key}</div>
                                  <div style={{ color: theme.textSecondary }}>{req.value}</div>
                                </div>
                              </ListRow>
                            ))
                          ) : (
                            <EmptyHint>Sin parámetros.</EmptyHint>
                          )}
                        </div>
              </CollapsibleSection>
            </div>

            {/* --- SECCIÓN BODY PARAMS (CORREGIDO type="button") --- */}
            <div className="col p-0 mt-4">
              <CollapsibleSection
                title="Parámetros de cuerpo (Opcional)"
                count={selectedNode.data.requestParamsBody?.length || 0}
                open={dropdownRequestBody}
                onToggle={() => handleDropdown('dropdownRequestBody')}
              >
                        {sectionActions('body')}
                        <div className="mt-2">
                          {selectedNode.data.requestParamsBody.length ? (
                            selectedNode.data.requestParamsBody.map((body, index) => (
                              <ListRow
                                key={index}
                                dragIndex={index}
                                onReorder={(from, to) => moveParam('requestParamsBody', from, to)}
                                revealActions
                                onRemove={() => {
                                  const filter = selectedNode.data.requestParamsBody.filter((v, i) => i !== index);
                                  handleSelectedNode((prev) => ({ ...prev, data: { ...prev.data, requestParamsBody: filter } }));
                                }}
                                removeTitle="Eliminar parámetro"
                              >
                                <div
                                  className="pointer"
                                  title="Editar"
                                  style={{ overflowWrap: 'anywhere' }}
                                  onClick={() => { setEditModal(true); handleModalParams('body', { ...body, index }); }}
                                >
                                  <div style={{ fontWeight: 600 }}>{body.key}</div>
                                  <div style={{ color: theme.textSecondary }}>{body.value}</div>
                                </div>
                              </ListRow>
                            ))
                          ) : (
                            <EmptyHint>Sin parámetros.</EmptyHint>
                          )}
                        </div>
              </CollapsibleSection>
            </div>

            {/* --- SECCIÓN RESPONSE PARAMS (CORREGIDO type="button") --- */}
            <div className="col p-0 mt-4">
              <CollapsibleSection
                title="Parámetros de respuesta (Opcional)"
                count={selectedNode.data.responseParams?.length || 0}
                open={dropdownResponse}
                onToggle={() => handleDropdown('dropdownResponse')}
              >
                        {sectionActions('response')}
                        <div className="mt-2">
                          {selectedNode.data.responseParams.length ? (
                            selectedNode.data.responseParams.map((res, index) => (
                              <ListRow
                                key={index}
                                dragIndex={index}
                                onReorder={(from, to) => moveParam('responseParams', from, to)}
                                revealActions
                                onRemove={() => {
                                  const filter = selectedNode.data.responseParams.filter((v, i) => i !== index);
                                  handleSelectedNode((prev) => ({ ...prev, data: { ...prev.data, responseParams: filter } }));
                                }}
                                removeTitle="Eliminar respuesta"
                              >
                                <div
                                  className="pointer"
                                  title="Editar"
                                  style={{ overflowWrap: 'anywhere' }}
                                  onClick={() => { setEditModal(true); handleModalParams('response', { ...res, index }); }}
                                >
                                  <div style={{ fontWeight: 600 }}>{renderVariableChips(res.key)}</div>
                                  <div style={{ color: theme.textSecondary }}>→ {renderVariableChips(res.value)}</div>
                                  {describeResponseTransform(res) && (
                                    <div style={{ fontSize: '12px', color: QUERY_API_NODE_COLOR, marginTop: '2px' }}>
                                      {describeResponseTransform(res)}
                                    </div>
                                  )}
                                </div>
                              </ListRow>
                            ))
                          ) : (
                            <EmptyHint>Sin respuestas mapeadas.</EmptyHint>
                          )}
                        </div>
              </CollapsibleSection>
            </div>

            <div className="col p-0 mt-2">
              <ChipButton icon={BiTransfer} onClick={() => { handleConnectionTestData({}); handleModalConnectionTest(true); handleConnectionTestDataParams({ url: getValues('url_api'), protocol: selectedNode.data.protocol.id }); }}>
                Consultar API
              </ChipButton>
            </div>
            </StepSection>

            <StepSection
              index={3}
              title="Respuesta"
              summary={stepSummary.response}
              state={stepState.response}
            >
            <div className="col p-0">
              <FieldLabel>Variables:</FieldLabel>
              <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, marginTop: 4, background: isDark ? '#111b21' : '#fff', overflow: 'hidden', resize: 'vertical', minHeight: 120, maxHeight: 600, height: 120, width: '100%', position: 'relative' }}>
                <AceEditor mode="json" theme={isDark ? 'monokai' : 'github'} value={(() => { const val = getValues('variables'); if (!val) return ''; if (typeof val === 'string') return val; if (typeof val === 'object') { try { return JSON.stringify(val, null, 2); } catch (e) { return String(val); } } return String(val); })()} onChange={(value) => setValue('variables', value, { shouldDirty: true })} width="100%" height="100%" fontSize={14} showPrintMargin={false} showGutter={true} highlightActiveLine={true} setOptions={{ enableBasicAutocompletion: false, enableLiveAutocompletion: false, enableSnippets: false, showLineNumbers: true, tabSize: 2, wrap: true, useWorker: false }} style={{ borderRadius: '4px' }} />
                {errors?.variables && (<span style={{ color: 'red', fontSize: 12 }}>* {errors.variables.message}</span>)}
              </div>
            </div>

            <div className="col p-0 mt-4">
              <SUTextField id="bot_response" name="bot_response" label="Respuesta del Bot:" maxLength={4096} placeholder="Escribe la respuesta del Bot" type="textArea" hint="Respuesta que se le envía al cliente." showEmoji showSticker showVariables selectedNode={selectedNode} contextVariables={contextVariables} getValues={getValues} setValue={setValue} errors={errors} {...register('bot_response')} />
            </div>

            <div className="d-flex align-items-center mb-3 mt-4">
              <input name="regularExpressions" id="regularExpressions" type="checkbox" className="cmn-toggle cmn-toggle-round-flat" value={selectedNode.data?.advanced?.regularExpressions?.active || false} checked={selectedNode.data?.advanced?.regularExpressions?.active || false} onChange={(e) => handleInputChange('advanced.regularExpressions.active', e.target.checked)} />
              <label className="mr-2 mb-0" htmlFor="regularExpressions"></label>
              <div style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>Expresiones regulares</div>
            </div>
            
            {selectedNode.data?.advanced?.regularExpressions?.active && (
              <div className="col p-0 mt-2 mb-4 panel-group collapsible">
                <div className="panel panel-default">
                  <div className="panel-heading p-2" style={{ border: `1px solid ${theme.border}`, borderRadius: '8px', backgroundColor: theme.inputBg, color: theme.textPrimary }}>
                    <div id="title-regularExpression" className="panel-title d-flex justify-content-between align-items-center pointer" onClick={() => handleDropdown('dropdownRegularExpression')}>
                      <span>Expresiones Regulares</span>
                      {dropdownRegularExpression ? <IoIosArrowDropup className="pointer mr-3" style={{ fontSize: '20px' }} /> : <IoIosArrowDropdown className="pointer mr-3" style={{ fontSize: '20px' }} />}
                    </div>
                  </div>
                  {dropdownRegularExpression && (
                    <div className="panel-collapse p-2" style={{ borderRadius: '4px' }}>
                      <div className="panel-body" style={{ paddingInline: 0 }}>
                        <div className="col p-0 mt-4">
                          <label>Tipo de expresión: </label>
                          <div className="su-control-group d-flex">
                            <Select styles={isDark == 'true' ? customStylesDark : customStyles} className="form-control basic-multi-select" placeholder="Seleccionar" value={selectedNode.data?.advanced?.regularExpressions?.type || false} name="expression_type" onChange={(value) => handleInputChange('advanced.regularExpressions.type', value)}
                              options={[{ label: 'Fecha', id: 'date' }, { label: 'Hora', id: 'hour' }, { label: 'Correo', id: 'mail' }, { label: 'Numérico', id: 'number' }, { label: 'Teléfono', id: 'phone' }]}
                              classNamePrefix="select" noOptionsMessage={() => 'Sin opciones'} getOptionLabel={(option) => (<div className="option d-flex" style={{ fontSize: '15px' }}>{option.label}</div>)} getOptionValue={(option) => option.id}
                            />
                          </div>
                        </div>
                        {!['number', 'mail', 'phone'].includes(selectedNode.data?.advanced?.regularExpressions?.type?.id) && (
                          <div className="col p-0 mt-4">
                            <label htmlFor="expressionFormat" style={{ color: theme.textSecondary, fontWeight: 600 }}>Formato: </label>
                            <div>
                              <Select styles={isDark == 'true' ? customStylesDark : customStyles} className="form-control basic-multi-select" placeholder="Seleccionar" value={selectedNode.data?.advanced?.regularExpressions?.format || false} name="expressionFormat" onChange={(value) => handleInputChange('advanced.regularExpressions.format', value)}
                                options={selectedNode.data?.advanced?.regularExpressions?.type?.id === 'date' ? [{ label: 'mm/dd/aaaa', id: 'mm/dd/aaaa' }, { label: 'dd/mm/aaaa', id: 'dd/mm/aaaa' }, { label: 'aaaa-mm-dd', id: 'aaaa-mm-dd' }] : [{ label: 'hh:mm', id: 'hh:mm' }]}
                                classNamePrefix="select" noOptionsMessage={() => 'Sin opciones'} getOptionLabel={(option) => (<div className="option d-flex" style={{ fontSize: '15px' }}>{option.label}</div>)} getOptionValue={(option) => option.id}
                              />
                            </div>
                          </div>
                        )}
                        <div className="col p-0 mt-4">
                          <label htmlFor="expressionErrorMessage" style={{ color: theme.textSecondary, fontWeight: 600 }}>Mensaje de error: </label>
                          <div>
                            <textarea className="form-control" id="expressionErrorMessage" name="expressionErrorMessage" type="text" style={{ width: '100%', height: '80px', borderRadius: '4px' }}
                              {...register('regular_expressions_error_message', { required: { value: !modalParams && selectedNode.data?.advanced?.regularExpressions?.active ? true : false, message: 'Campo requerido.' }, pattern: modalParams ? undefined : { value: /\S+/, message: 'No puede ser solo espacios en blanco' } })}
                            />
                            {errors.regular_expressions_error_message && <Alert>* {errors.regular_expressions_error_message.message}</Alert>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <FormSection title="Estados de la Solicitud" style={{ marginTop: '8px' }}>
              <div className="col p-0">
                <FieldLabel htmlFor="succes">Solicitud exitosa:</FieldLabel>
                <div className="d-flex align-items-center">
                  <FaCheckCircle className="mr-1" style={{ fontSize: '22px', color: '#2fae4e' }} />
                  <input className="form-control" id="succes" name="succes" type="text" style={{ width: '100%', height: '35px' }} value={selectedNode.data.succes || ''} readOnly />
                </div>
              </div>
              <div className="col p-0 mt-2">
                <FieldLabel htmlFor="failed">Solicitud fallida:</FieldLabel>
                <div className="d-flex align-items-center">
                  <BiSolidError className="mr-1" style={{ fontSize: '22px', color: '#FF2242' }} />
                  <input className="form-control" id="failed" name="failed" type="text" style={{ width: '100%', height: '35px' }} value={selectedNode.data.failed || ''} readOnly />
                </div>
              </div>
            </FormSection>
            </StepSection>
          </div>
        </form>
      </NodeEditorChrome>

      <NodeModal
        open={modalParams}
        onClose={() => {
          setModalParams(false);
          setTempEditData(null);
          setEditModal(false);
          setParameterType(null);
        }}
        width={500}
        isDark={isDark}
        icon={BsDatabaseFillAdd}
        color={QUERY_API_NODE_COLOR}
        eyebrow="Consulta API"
        title={
          parameterType === 'response'
            ? editModal
              ? 'Editar mapeo de respuesta'
              : 'Nuevo mapeo de respuesta'
            : editModal
            ? 'Editar parámetro'
            : 'Nuevo parámetro'
        }
      >
        {modalParams && parameterType && (
          <form onSubmit={handleSubmit(handleDataParams)}>
            <div>
              {/* CAMPO OCULTO DEL INDICE */}
              <input type="hidden" {...register(`${parameterType}_index`)} />

              <div className="col">
                <SUTextField id={`${parameterType}_key`} name={`${parameterType}_key`} label={parameterType === 'response' ? "Origen del dato:" : "Parámetro:"} type="text" showVariables={parameterType === 'response'} typeVariables={['api']} contextVariables={contextVariables} selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                  {...register(`${parameterType}_key`, { required: { value: modalParams, message: 'Campo requerido' } })}
                />
              </div>

              <div className="col mt-3">
                <SUTextField id={`${parameterType}_value`} name={`${parameterType}_value`} label={parameterType === 'response' ? "Variable de destino (donde se guarda):" : "Valor:"} type="text" showVariables={parameterType !== 'headers'} typeVariables={['general']} contextVariables={contextVariables} selectedNode={selectedNode} getValues={getValues} setValue={setValue} errors={errors}
                  {...register(`${parameterType}_value`, { required: { value: modalParams, message: 'Campo requerido' } })}
                />
              </div>

              {parameterType === 'response' && (
                <div className="col mt-4 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                  <label className="d-flex align-items-center mb-2" style={{ fontWeight: 600, fontSize: '12px', letterSpacing: '0.06em', color: QUERY_API_NODE_COLOR }}>
                    <BsGearFill className="mr-2" /> TRANSFORMACIÓN DE DATO
                  </label>
                  <div className="mb-3">
                    <KitSelect
                      theme={theme}
                      ariaLabel="Transformación de dato"
                      value={watch(`${parameterType}_source`) || 'user_message'}
                      onChange={(value) => setValue(`${parameterType}_source`, value)}
                      options={[
                        { value: 'user_message', label: 'Usar valor de la API' },
                        { value: 'manual', label: 'Asignar valor fijo manual' },
                        { value: 'clear', label: 'Limpiar / Vaciar campo' },
                      ]}
                    />
                  </div>
                  {watch(`${parameterType}_source`) === 'manual' && (
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Valor fijo a asignar..."
                        style={{ backgroundColor: theme.inputBg, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.textPrimary }}
                        {...register(`${parameterType}_manualValue`)}
                      />
                    </div>
                  )}
                  {(watch(`${parameterType}_source`) || 'user_message') === 'user_message' && (
                    <ValueFiltersEditor
                      filters={responseFilters}
                      onChange={setResponseFilters}
                      isDark={isDark}
                      accentColor={QUERY_API_NODE_COLOR}
                    />
                  )}
                </div>
              )}

              <div className="d-flex justify-content-end mt-4" style={{ gap: '8px' }}>
                <NodeModalButton
                  isDark={isDark}
                  onClick={() => { setModalParams(false); setTempEditData(null); setEditModal(false); setParameterType(null); }}
                >
                  Cancelar
                </NodeModalButton>
                <NodeModalButton
                  type="submit"
                  variant="primary"
                  color={QUERY_API_NODE_COLOR}
                  isDark={isDark}
                >
                  Guardar
                </NodeModalButton>
              </div>
            </div>
          </form>
        )}
      </NodeModal>

      <NodeModal
        open={Boolean(bulkTarget)}
        onClose={closeBulk}
        width={520}
        isDark={isDark}
        icon={LuClipboardPaste}
        color={QUERY_API_NODE_COLOR}
        eyebrow="Consulta API"
        title={`Pegar lista de ${
          bulkTarget ? PARAM_SECTIONS[bulkTarget].nounPlural : ''
        }`}
      >
        <p style={{ fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>
          Una línea por elemento. Acepta <b>clave: valor</b>, <b>clave=valor</b> o
          pegado directo desde una planilla.
        </p>
        <textarea
          className="form-control"
          autoFocus
          spellCheck={false}
          placeholder={'travelId: {{webHook.travelId}}\nstatus: {{webHook.serviceStatus}}'}
          style={{
            width: '100%',
            height: '170px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '12px',
          }}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
        />

        {bulkText.trim() !== '' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '10px',
              padding: '9px 11px',
              borderRadius: '8px',
              borderLeft: `3px solid ${bulkRows.length ? QUERY_API_NODE_COLOR : '#FF2242'}`,
              backgroundColor: `${bulkRows.length ? QUERY_API_NODE_COLOR : '#FF2242'}14`,
              fontSize: '12px',
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            {bulkRows.length
              ? `Se detectaron ${bulkRows.length} ${PARAM_SECTIONS[bulkTarget].nounPlural} válidos.`
              : 'No se detectó ningún par clave / valor.'}
          </div>
        )}

        <div className="d-flex align-items-center mt-3">
          <input
            id="bulkReplace"
            name="bulkReplace"
            type="checkbox"
            className="cmn-toggle cmn-toggle-round-flat"
            checked={bulkReplace}
            onChange={(e) => setBulkReplace(e.target.checked)}
          />
          <label className="mr-2 mb-0" htmlFor="bulkReplace"></label>
          <div style={{ fontSize: 12, color: theme.textSecondary }}>
            Reemplazar los{' '}
            {bulkTarget
              ? selectedNode.data?.[PARAM_SECTIONS[bulkTarget].dataKey]?.length || 0
              : 0}{' '}
            existentes. Si no, se agregan al final.
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4" style={{ gap: '8px' }}>
          <NodeModalButton isDark={isDark} onClick={closeBulk}>
            Cancelar
          </NodeModalButton>
          <NodeModalButton
            variant="primary"
            color={QUERY_API_NODE_COLOR}
            isDark={isDark}
            disabled={!bulkRows.length}
            onClick={applyBulk}
          >
            {bulkRows.length ? `Agregar ${bulkRows.length}` : 'Agregar'}
          </NodeModalButton>
        </div>
      </NodeModal>
    </>
  );
};

export default QueryApi;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
`;